import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Button, Drawer, Grid, MobileStepper, Typography } from '@mui/material';
import { addYears } from 'date-fns';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { EmployeeApi } from '../../api';
import { isBreakpointMobile, useBreakpoint } from '../../breakpoint';
import { checkHolidayOverlap, getDateWithoutTimeInUtc, getHolidayLength, } from '../../functions';
import { ReducerContext, useHoliday } from '../../hooks';
import { theme } from '../../themes';
import { HolidayBooking } from '../../types/holidayTypes';
import { CalendarContext } from '../calendarContext';
import { CloseDialogButton } from '../CloseDialogButton';
import { NewBookingContext } from '../NewBookingContext';
import { AddAbsenceConfirmationPage } from './AddAbsenceConfirmationPage';
import { AddAbsenceMultipleDayPage } from './AddAbsenceMultipleDayPage';
import { AddAbsenceNumberOfDaysPage } from './AddAbsenceNumberOfDaysPage';
import { AddAbsencePeriodPage } from './AddAbsencePeriodPage';
import { AddAbsenceTitleDialog } from './AddAbsenceTitleDialog';
var PAGES;
(function (PAGES) {
    PAGES["NUMBER_OF_DAYS"] = "NUMBER_OF_DAYS";
    PAGES["MULTIPLE_DAY_SELECTION"] = "MULTIPLE_DAY_SELECTION";
    PAGES["PERIOD_SELECTION"] = "PERIOD_SELECTION";
    PAGES["CONFIRMATION"] = "CONFIRMATION";
})(PAGES || (PAGES = {}));
const multipleDayPages = [
    PAGES.NUMBER_OF_DAYS,
    PAGES.MULTIPLE_DAY_SELECTION,
    PAGES.PERIOD_SELECTION,
    PAGES.CONFIRMATION,
];
const singleDayPages = [PAGES.NUMBER_OF_DAYS, PAGES.PERIOD_SELECTION, PAGES.CONFIRMATION];
export const AddAbsenceDrawer = ({ openDrawer, setOpenDrawer, date, }) => {
    const mainTextColor = theme.palette.primary.main;
    const breakpoint = useBreakpoint();
    const isMobile = isBreakpointMobile(breakpoint);
    const { submitHoliday } = useHoliday(EmployeeApi);
    const { userSettingsState, holidayState } = useContext(ReducerContext);
    const { calendarSummaryValues, freeDays, onSubmitHolidayBooking } = useContext(CalendarContext);
    const [page, setPage] = useState(0);
    const [pages, setPages] = useState(multipleDayPages);
    const [multipleDays, setMultipleDays] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState([]);
    const [isInFutureEntitlementYear, setIsInFutureEntitlementYear] = useState(false);
    const [showFutureEntitlementYearInformation, setShowFutureEntitlementYearInformation] = useState(false);
    const selectedDate = date.format('D MMMM YYYY');
    const { dateRange, startInAfternoon, isManagerApprovalRequired, deductFromEntitlement, endInMorning, updateState, } = useContext(NewBookingContext);
    const duration = useMemo(() => {
        if (!dateRange.startDate) {
            return 0;
        }
        return getHolidayLength(dateRange.startDate.toDate(), dateRange.endDate?.toDate() ?? dateRange.startDate.toDate(), startInAfternoon, endInMorning, userSettingsState.workingDays, freeDays);
    }, [
        dateRange.startDate,
        dateRange.endDate,
        startInAfternoon,
        endInMorning,
        userSettingsState.workingDays,
        freeDays,
    ]);
    const onCancel = () => {
        cleanup();
        setOpenDrawer(false);
    };
    useEffect(() => {
        if (!dateRange.startDate) {
            return;
        }
        const dateFromFutureEntitlementYearSelected = (dateRange.endDate?.toDate() ?? dateRange.startDate.toDate()) >
            new Date(userSettingsState.end);
        setShowFutureEntitlementYearInformation(dateFromFutureEntitlementYearSelected);
    }, [dateRange, userSettingsState.end]);
    const currentPage = useCallback(() => {
        const newPage = multipleDays ? multipleDayPages[page] : singleDayPages[page];
        switch (newPage) {
            case PAGES.NUMBER_OF_DAYS:
                return (_jsx(AddAbsenceNumberOfDaysPage, { isMultipleDays: multipleDays, setMultipleDays: setMultipleDays }));
            case PAGES.MULTIPLE_DAY_SELECTION:
                return _jsx(AddAbsenceMultipleDayPage, { duration: duration });
            case PAGES.PERIOD_SELECTION:
                return _jsx(AddAbsencePeriodPage, { duration: duration });
            case PAGES.CONFIRMATION:
                return (_jsx(AddAbsenceConfirmationPage, { duration: duration, setInfoMessage: setInfoMessage, setErrorMessage: setErrorMessage }));
            default:
                return _jsx(_Fragment, {});
        }
    }, [duration, multipleDays, page]);
    const cleanup = () => {
        setPages(multipleDayPages);
        setPage(0);
        setErrorMessage('');
        setInfoMessage([]);
        setMultipleDays(null);
        setIsInFutureEntitlementYear(false);
        setShowFutureEntitlementYearInformation(false);
        updateState({
            dateRange: { startDate: null, endDate: null },
            startInAfternoon: false,
            endInMorning: false,
            isManagerApprovalRequired: false,
            deductFromEntitlement: false,
        });
    };
    const onBack = useCallback(() => {
        setInfoMessage([]);
        setErrorMessage('');
        if (page === 0) {
            cleanup();
            setOpenDrawer(false);
            return;
        }
        const newPage = multipleDays
            ? multipleDayPages.indexOf(multipleDayPages[page])
            : singleDayPages.indexOf(singleDayPages[page]);
        setPage(newPage - 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, multipleDays, setOpenDrawer]);
    const onSubmitHolidaySuccess = () => {
        cleanup();
        onSubmitHolidayBooking();
        setOpenDrawer(false);
    };
    const onNext = useCallback(async () => {
        setInfoMessage([]);
        if (page >= pages.findIndex((p) => p === PAGES.NUMBER_OF_DAYS) &&
            multipleDays === null) {
            return setErrorMessage('Please select an option first before continuing.');
        }
        const endOfCurrentHolidayYear = new Date(userSettingsState.holidayYearEnd);
        const endOfLastHolidayYear = addYears(endOfCurrentHolidayYear, -1);
        if (!!multipleDays &&
            page >= pages.findIndex((p) => p === PAGES.MULTIPLE_DAY_SELECTION) &&
            dateRange.endDate <= dateRange.startDate) {
            return setErrorMessage('End date should be later than Start date.');
        }
        if (page >=
            pages.findIndex((p) => p === PAGES.MULTIPLE_DAY_SELECTION || p === PAGES.PERIOD_SELECTION)) {
            if (dateRange.endDate === null || dateRange.endDate === undefined) {
                setErrorMessage('End date is required.');
                return;
            }
            if (dateRange.startDate < moment(endOfLastHolidayYear) &&
                dateRange.endDate > moment(endOfLastHolidayYear)) {
                setErrorMessage("You can't book absences that last through previous and current entitlement year.");
                return;
            }
            if (showFutureEntitlementYearInformation) {
                setInfoMessage((state) => [
                    ...state,
                    "You are booking an absence in a future entitlement year, this absence won't impact your current year's entitlement.",
                ]);
            }
            const daysInRange = getHolidayLength(dateRange.startDate.toDate(), dateRange.endDate.toDate(), startInAfternoon, endInMorning, userSettingsState.workingDays, freeDays);
            if (daysInRange === 0) {
                setErrorMessage('The dates you have selected do not draw against your entitlement.');
                return;
            }
            if (!deductFromEntitlement && isManagerApprovalRequired) {
                setErrorMessage('To send an approval request, your absence must deduct from your entitlement.');
                return;
            }
            if (!isInFutureEntitlementYear &&
                deductFromEntitlement &&
                calendarSummaryValues &&
                daysInRange > calendarSummaryValues.remaining) {
                setErrorMessage('Not enough days remaining!');
                return;
            }
            if (checkHolidayOverlap(holidayState.holidayBookings, dateRange.startDate.toDate(), dateRange.endDate.toDate(), startInAfternoon, endInMorning)) {
                setErrorMessage('Conflict with other absences!');
                return;
            }
        }
        if (page >= pages.findIndex((p) => p === PAGES.CONFIRMATION)) {
            try {
                const bookingForm = new HolidayBooking();
                bookingForm.startDate = getDateWithoutTimeInUtc(dateRange.startDate.toDate());
                bookingForm.endDate = getDateWithoutTimeInUtc(dateRange.endDate.toDate());
                bookingForm.isStartHalfDay = startInAfternoon;
                bookingForm.isEndHalfDay = endInMorning;
                bookingForm.isManagerApprovalRequired = isManagerApprovalRequired;
                bookingForm.deductFromEntitlement = deductFromEntitlement;
                await submitHoliday(onSubmitHolidaySuccess, bookingForm);
            }
            catch (e) {
                setErrorMessage('Cannot submit booking.');
            }
            return;
        }
        setErrorMessage('');
        const newPage = multipleDays
            ? multipleDayPages.indexOf(multipleDayPages[page])
            : singleDayPages.indexOf(singleDayPages[page]);
        setPage(newPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        page,
        pages,
        multipleDays,
        userSettingsState.holidayYearEnd,
        userSettingsState.workingDays,
        dateRange.endDate,
        dateRange.startDate,
        showFutureEntitlementYearInformation,
        startInAfternoon,
        endInMorning,
        freeDays,
        deductFromEntitlement,
        isManagerApprovalRequired,
        isInFutureEntitlementYear,
        calendarSummaryValues,
        holidayState.holidayBookings,
        submitHoliday,
        onSubmitHolidayBooking,
        setOpenDrawer,
    ]);
    useEffect(() => {
        const dateFromFutureEntitlementYearSelected = dateRange.endDate > moment(userSettingsState.end);
        setIsInFutureEntitlementYear(dateFromFutureEntitlementYearSelected);
    }, [dateRange, userSettingsState.end]);
    useEffect(() => {
        if (multipleDays !== null) {
            setErrorMessage('');
        }
        if (multipleDays) {
            setPages(multipleDayPages);
        }
        else {
            setPages(singleDayPages);
        }
    }, [multipleDays]);
    const onInfoClose = (info) => {
        setInfoMessage((state) => state.filter((s) => s !== info));
    };
    const navbarHeight = document.getElementById('navbar-template')?.clientHeight ?? 0;
    return (_jsxs(Drawer, { style: { bottom: 'unset' }, anchor: 'top', open: openDrawer, onClose: onCancel, hideBackdrop: true, PaperProps: {
            sx: { width: isMobile ? '100%' : '30%', margin: '0 auto' },
        }, sx: {
            '& .MuiDrawer-paper': { top: `${navbarHeight + 1}px` },
        }, children: [_jsx(AddAbsenceTitleDialog, { children: _jsxs(Grid, { container: true, children: [_jsxs(Grid, { item: true, xs: 6, mb: 'auto', children: [_jsx(Typography, { variant: "body1", fontWeight: 'bold', children: selectedDate }), _jsx(Typography, { variant: 'subtitle2', children: "working day" })] }), _jsx(Grid, { item: true, xs: 6, display: 'flex', justifyContent: "end", alignItems: "center", mb: 'auto', mt: 'auto', pr: 5, children: _jsxs(Typography, { width: 'fit-content', variant: 'subtitle2', color: 'white', bgcolor: theme.palette.primary.main, borderRadius: '5px', p: '2px 3px', textAlign: 'center', children: [userSettingsState.remainingHolidays, " remaining days"] }) }), _jsx(Grid, { item: true, xs: 2, children: _jsx(CloseDialogButton, { onClose: onCancel }) })] }) }), currentPage(), errorMessage && (_jsx(Alert, { severity: 'error', children: _jsx(Typography, { variant: 'subtitle2', children: errorMessage }) })), infoMessage.map((info, index) => (_jsx(Alert, { sx: { mb: 1 }, severity: 'info', onClose: () => onInfoClose(info), children: _jsx(Typography, { variant: 'subtitle2', children: info }) }, `${index}-${info}`))), _jsx(MobileStepper, { variant: "dots", steps: pages.length, position: "static", activeStep: page, sx: { flexGrow: 1 }, nextButton: _jsx(Button, { sx: { '&.Mui-disabled': { color: mainTextColor } }, disabled: errorMessage.length > 0, size: "small", variant: 'outlined', onClick: onNext, children: page === pages.length - 1 ? 'Submit' : 'Next' }), backButton: _jsx(Button, { size: "small", onClick: onBack, children: page === 0 ? 'Cancel' : 'Back' }) })] }));
};
